import { useEffect, useState, type ReactNode } from "react"
import { Check, CheckCircle2 } from "lucide-react"
import { cn } from "@/lib/utils"
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../Dialog/Dialog"
import { Button } from "../Button/Button"

export type PaywallDialogBilling = "monthly" | "annual"
export type PaywallDialogStage = "picker" | "checkout" | "success"

export interface PaywallDialogPlanUsage {
  label: string
  value: string
}

export interface PaywallDialogPlan {
  id: string
  name: string
  tagline?: string
  priceMonthly: number
  priceAnnualPerMonth?: number
  priceAnnualTotal?: number
  /** Compact spec rows shown above the feature list — the tier's cost meters. */
  usage?: PaywallDialogPlanUsage[]
  features: string[]
  ctaLabel?: string
  /** Highlights the card with a "Most popular" chip and an amber CTA. */
  popular?: boolean
}

export interface PaywallDialogCheckoutHelpers {
  plan: PaywallDialogPlan
  billing: PaywallDialogBilling
  onSuccess: () => void
  onBack: () => void
}

export interface PaywallDialogProps {
  open: boolean
  onOpenChange: (open: boolean, stage: PaywallDialogStage) => void
  plans: PaywallDialogPlan[]
  pickerTitle?: string
  pickerDescription?: string
  showBillingToggle?: boolean
  billing?: PaywallDialogBilling
  defaultBilling?: PaywallDialogBilling
  onBillingChange?: (next: PaywallDialogBilling) => void
  selectedPlanId?: string | null
  defaultSelectedPlanId?: string | null
  onSelectPlan?: (plan: PaywallDialogPlan | null) => void
  checkoutSlot?: (helpers: PaywallDialogCheckoutHelpers) => ReactNode
  checkoutTitle?: (plan: PaywallDialogPlan) => string
  checkoutDescription?: string
  backToPickerLabel?: string
  succeeded?: boolean
  defaultSucceeded?: boolean
  onSucceededChange?: (next: boolean) => void
  successTitle?: string
  successHeading?: string
  successDescription?: (plan: PaywallDialogPlan | null) => ReactNode
  monthlyLabel?: string
  annualLabel?: string
  annualSaveLabel?: string
  pricePerMonthSuffix?: string
  freePriceLabel?: string
  annualNote?: (annualTotal: number) => ReactNode
  currencySymbol?: string
  currentPlanId?: string | null
  currentPlanLabel?: string
  /** Reassurance line under the plan grid. Pass null to hide it. */
  pickerFooterNote?: ReactNode
  className?: string
}

// The featured plan's CTA borrows the sidebar Upgrade button's amber gradient
// so it anchors the eye — every other card stays monochrome.
const POPULAR_CTA_CLASSES =
  "text-zinc-950 bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 hover:from-amber-300 hover:via-amber-400 hover:to-amber-500 border border-amber-300/60"

const MOBILE_FULLSCREEN_CLASSES =
  "max-sm:top-0 max-sm:bottom-0 max-sm:left-0 max-sm:right-0 max-sm:translate-x-0 max-sm:translate-y-0 max-sm:w-full max-sm:max-w-full max-sm:h-dvh max-sm:max-h-dvh max-sm:rounded-none max-sm:pb-[max(1.5rem,env(safe-area-inset-bottom))]"

function formatPrice(price: number, freeLabel: string, currencySymbol: string) {
  if (price === 0) return freeLabel
  return `${currencySymbol}${Number.isInteger(price) ? price : price.toFixed(2)}`
}

export function PaywallDialog({
  open,
  onOpenChange,
  plans,
  pickerTitle = "Pick a plan",
  pickerDescription = "Unlock the full feature set. Cancel anytime.",
  showBillingToggle = true,
  billing,
  defaultBilling = "monthly",
  onBillingChange,
  selectedPlanId,
  defaultSelectedPlanId = null,
  onSelectPlan,
  checkoutSlot,
  checkoutTitle = (plan) => `Start ${plan.name}`,
  checkoutDescription = "Enter payment details to complete your subscription.",
  backToPickerLabel = "\u2190 Pick a different plan",
  succeeded,
  defaultSucceeded = false,
  onSucceededChange,
  successTitle = "You're subscribed",
  successHeading = "Subscription active",
  successDescription,
  monthlyLabel = "Monthly",
  annualLabel = "Annually",
  annualSaveLabel = "Save 20%",
  pricePerMonthSuffix = "/mo",
  freePriceLabel = "$0",
  annualNote,
  currencySymbol = "$",
  currentPlanId = null,
  currentPlanLabel = "Current plan",
  pickerFooterNote = "Everything you've built moves with you between plans.",
  className,
}: PaywallDialogProps) {
  const [internalBilling, setInternalBilling] = useState<PaywallDialogBilling>(defaultBilling)
  const [internalSelectedId, setInternalSelectedId] = useState<string | null>(
    defaultSelectedPlanId,
  )
  const [internalSucceeded, setInternalSucceeded] = useState(defaultSucceeded)

  const isBillingControlled = billing !== undefined
  const currentBilling = isBillingControlled ? billing : internalBilling

  const isSelectedControlled = selectedPlanId !== undefined
  const currentSelectedId = isSelectedControlled ? selectedPlanId : internalSelectedId

  const isSucceededControlled = succeeded !== undefined
  const currentSucceeded = isSucceededControlled ? succeeded : internalSucceeded

  // Reset on fresh opens so the next trigger starts at the picker (unless
  // the consumer has pinned the state via controlled props or defaults).
  useEffect(() => {
    if (!open) return
    if (!isSelectedControlled) setInternalSelectedId(defaultSelectedPlanId)
    if (!isBillingControlled) setInternalBilling(defaultBilling)
    if (!isSucceededControlled) setInternalSucceeded(defaultSucceeded)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const currentPlan =
    plans.find((p) => p.id === currentSelectedId) ?? null

  const stage: PaywallDialogStage = currentSucceeded
    ? "success"
    : currentPlan
      ? "checkout"
      : "picker"

  const handleSelectBilling = (next: PaywallDialogBilling) => {
    if (!isBillingControlled) setInternalBilling(next)
    onBillingChange?.(next)
  }

  const handleSelectPlan = (plan: PaywallDialogPlan | null) => {
    if (!isSelectedControlled) setInternalSelectedId(plan?.id ?? null)
    onSelectPlan?.(plan)
  }

  const handleSuccess = () => {
    if (!isSucceededControlled) setInternalSucceeded(true)
    onSucceededChange?.(true)
  }

  const handleBackToPicker = () => handleSelectPlan(null)

  const handleOpenChange = (next: boolean) => {
    onOpenChange(next, stage)
  }

  const headline = currentSucceeded
    ? successTitle
    : currentPlan
      ? checkoutTitle(currentPlan)
      : pickerTitle

  const subhead = currentSucceeded
    ? undefined
    : currentPlan
      ? checkoutDescription
      : pickerDescription

  const onPicker = stage === "picker"

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={cn(
          "v1 bg-card max-w-[960px] sm:max-w-[960px] w-[96vw] max-h-[90dvh] overflow-y-auto p-6",
          MOBILE_FULLSCREEN_CLASSES,
          className,
        )}
        overlayClassName="bg-black/60 backdrop-blur-[2px]"
      >
        <div
          className={cn(
            "mb-4",
            onPicker && showBillingToggle &&
              "sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 flex flex-col gap-3",
          )}
        >
          <div className={cn(onPicker && showBillingToggle && "sm:col-span-1")}>
            <DialogTitle className="text-page-title text-foreground">
              {headline}
            </DialogTitle>
            {subhead && (
              <DialogDescription className="text-caption text-muted-foreground mt-0.5">
                {subhead}
              </DialogDescription>
            )}
          </div>
          {onPicker && showBillingToggle && (
            <>
              <div className="sm:col-span-1 flex sm:justify-center">
                <BillingToggle
                  value={currentBilling}
                  onChange={handleSelectBilling}
                  monthlyLabel={monthlyLabel}
                  annualLabel={annualLabel}
                  annualSaveLabel={annualSaveLabel}
                />
              </div>
              <div className="hidden sm:block sm:col-span-1" />
            </>
          )}
        </div>

        {stage === "success" ? (
          <div className="motion-safe:animate-in motion-safe:slide-in-from-right motion-safe:fade-in motion-safe:duration-500 flex flex-col items-center justify-center py-12 px-6 text-center">
            <CheckCircle2
              size={56}
              strokeWidth={1.5}
              className="text-success mb-5"
            />
            <h3 className="text-section text-foreground">{successHeading}</h3>
            {successDescription && (
              <p className="text-body text-muted-foreground mt-2 max-w-sm text-balance">
                {successDescription(currentPlan)}
              </p>
            )}
          </div>
        ) : stage === "checkout" && currentPlan ? (
          <div className="flex flex-col gap-4">
            <button
              type="button"
              onClick={handleBackToPicker}
              className="text-caption text-muted-foreground hover:text-foreground cursor-pointer self-start motion-safe:active:scale-[0.97] motion-safe:transition-transform motion-safe:duration-100"
            >
              {backToPickerLabel}
            </button>
            {checkoutSlot?.({
              plan: currentPlan,
              billing: currentBilling,
              onSuccess: handleSuccess,
              onBack: handleBackToPicker,
            })}
          </div>
        ) : (
          <PlanPicker
            plans={plans}
            billing={currentBilling}
            onSelect={handleSelectPlan}
            pricePerMonthSuffix={pricePerMonthSuffix}
            freePriceLabel={freePriceLabel}
            annualNote={annualNote}
            currencySymbol={currencySymbol}
            currentPlanId={currentPlanId}
            currentPlanLabel={currentPlanLabel}
            footerNote={pickerFooterNote}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

interface BillingToggleProps {
  value: PaywallDialogBilling
  onChange: (next: PaywallDialogBilling) => void
  monthlyLabel: string
  annualLabel: string
  annualSaveLabel: string
}

function BillingToggle({
  value,
  onChange,
  monthlyLabel,
  annualLabel,
  annualSaveLabel,
}: BillingToggleProps) {
  return (
    <div className="inline-flex items-center justify-center gap-3 text-caption">
      <button
        type="button"
        onClick={() => onChange("monthly")}
        className={cn(
          "cursor-pointer transition-colors",
          value === "monthly"
            ? "text-foreground font-medium"
            : "text-muted-foreground hover:text-foreground",
        )}
      >
        {monthlyLabel}
      </button>
      <button
        type="button"
        onClick={() => onChange(value === "annual" ? "monthly" : "annual")}
        aria-label="Toggle billing cycle"
        className={cn(
          "relative w-10 h-5 rounded-full transition-colors cursor-pointer",
          value === "annual" ? "bg-primary" : "bg-muted border border-border",
        )}
      >
        <span
          className={cn(
            "absolute top-0.5 h-4 w-4 rounded-full bg-background shadow transition-all motion-safe:duration-200",
            value === "annual" ? "left-[22px]" : "left-0.5",
          )}
        />
      </button>
      <button
        type="button"
        onClick={() => onChange("annual")}
        className={cn(
          "cursor-pointer transition-colors inline-flex items-center gap-1.5",
          value === "annual"
            ? "text-foreground font-medium"
            : "text-muted-foreground hover:text-foreground",
        )}
      >
        {annualLabel}
        <span className="text-caption text-primary border border-primary/30 rounded px-1.5 py-0.5">
          {annualSaveLabel}
        </span>
      </button>
    </div>
  )
}

interface PlanPickerProps {
  plans: PaywallDialogPlan[]
  billing: PaywallDialogBilling
  onSelect: (plan: PaywallDialogPlan) => void
  pricePerMonthSuffix: string
  freePriceLabel: string
  annualNote?: (annualTotal: number) => ReactNode
  currencySymbol: string
  currentPlanId: string | null
  currentPlanLabel: string
  footerNote?: ReactNode
}

function PlanPicker({
  plans,
  billing,
  onSelect,
  pricePerMonthSuffix,
  freePriceLabel,
  annualNote,
  currencySymbol,
  currentPlanId,
  currentPlanLabel,
  footerNote,
}: PlanPickerProps) {
  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {plans.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            billing={billing}
            onSelect={() => onSelect(plan)}
            pricePerMonthSuffix={pricePerMonthSuffix}
            freePriceLabel={freePriceLabel}
            annualNote={annualNote}
            currencySymbol={currencySymbol}
            isCurrent={plan.id === currentPlanId}
            currentPlanLabel={currentPlanLabel}
          />
        ))}
      </div>
      {footerNote && (
        <p className="mt-4 text-center text-caption text-muted-foreground">
          {footerNote}
        </p>
      )}
    </div>
  )
}

interface PlanCardProps {
  plan: PaywallDialogPlan
  billing: PaywallDialogBilling
  onSelect: () => void
  pricePerMonthSuffix: string
  freePriceLabel: string
  annualNote?: (annualTotal: number) => ReactNode
  currencySymbol: string
  isCurrent: boolean
  currentPlanLabel: string
}

function PlanCard({
  plan,
  billing,
  onSelect,
  pricePerMonthSuffix,
  freePriceLabel,
  annualNote,
  currencySymbol,
  isCurrent,
  currentPlanLabel,
}: PlanCardProps) {
  const isAnnual = billing === "annual"
  const price = isAnnual
    ? plan.priceAnnualPerMonth ?? plan.priceMonthly
    : plan.priceMonthly
  const priceLabel = formatPrice(price, freePriceLabel, currencySymbol)
  const annualNoteText =
    isAnnual && plan.priceAnnualTotal && plan.priceAnnualTotal > 0
      ? annualNote
        ? annualNote(plan.priceAnnualTotal)
        : `Billed at ${currencySymbol}${plan.priceAnnualTotal.toFixed(2)} annually`
      : null

  return (
    <div className="flex flex-col gap-3 rounded-md border border-border bg-card p-4">
      <div>
        <div className="flex items-center gap-2">
          <h3 className="text-section text-foreground">{plan.name}</h3>
          {plan.popular && (
            <span className="text-eyebrow text-primary border border-primary/30 rounded px-1.5 py-0.5">
              Most popular
            </span>
          )}
        </div>
        {plan.tagline && (
          <p className="text-caption text-muted-foreground mt-0.5">
            {plan.tagline}
          </p>
        )}
      </div>
      <div className="flex items-baseline gap-1">
        <span className="text-page-title text-foreground font-semibold">
          {priceLabel}
        </span>
        <span className="text-caption text-muted-foreground">
          {price > 0 ? pricePerMonthSuffix : ""}
        </span>
      </div>
      {annualNoteText && (
        <p className="text-caption text-muted-foreground -mt-2">{annualNoteText}</p>
      )}
      {plan.usage && plan.usage.length > 0 && (
        <div className="rounded-md border border-border bg-muted/40 px-3 py-2.5 flex flex-col gap-1.5">
          {plan.usage.map((row) => (
            <div key={row.label} className="flex items-baseline justify-between gap-2">
              <span className="text-caption text-muted-foreground">{row.label}</span>
              <span className="text-caption text-foreground font-medium tabular-nums whitespace-nowrap">
                {row.value}
              </span>
            </div>
          ))}
        </div>
      )}
      <ul className="flex flex-col gap-1.5 text-caption text-foreground flex-1">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-1.5">
            <Check
              size={12}
              strokeWidth={2}
              className="text-muted-foreground shrink-0 mt-0.5"
            />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      {isCurrent ? (
        <div className="w-full text-center text-label text-muted-foreground py-2">
          {currentPlanLabel}
        </div>
      ) : (
        <Button
          size="sm"
          onClick={onSelect}
          className={cn(
            "w-full text-label motion-safe:active:scale-[0.97] motion-safe:transition-transform motion-safe:duration-100",
            plan.popular && POPULAR_CTA_CLASSES,
          )}
        >
          {plan.ctaLabel ?? `Start ${plan.name}`}
        </Button>
      )}
    </div>
  )
}
